'use client';
import * as React from 'react';
import { BreadcrumbItem } from '../types/BreadcrumbItem';
import { Icon } from './icons/Icon';
import { cn } from '../utils';
import { useTranslation } from '../providers/UIProvider';
import { AnimatePresence, motion } from 'framer-motion';

export interface BreadcrumbProps {
  component?: React.ElementType;
  className?: string;
  route: BreadcrumbItem[];
  testId?: string;
}

export const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>(
  ({ className, route, component: Component = 'a', testId, ...props }, ref) => {
    const t = useTranslation();
    return (
      <nav
        ref={ref}
        aria-label='Breadcrumb'
        data-testid={testId}
        {...props}
        className={cn(className, 'inline-flex items-center flex-nowrap transition-all')}
      >
        {route.map((r, index) =>
          index !== route.length - 1 ? (
            <React.Fragment key={`${index}-frag`}>
              <Component
                key={`a-${index}`}
                className={cn('text-foreground-base', 'hover:text-info-text')}
                href={r.href}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  r.onClick?.();
                }}
              >
                {r.locKey ? t(`breadcrumb.${r.locKey}`) : r.label}
              </Component>
              <Icon
                key={`i-${index}`}
                name='chevron-down'
                className='px-xs'
                aria-hidden='true'
                rotate={270}
              ></Icon>
            </React.Fragment>
          ) : (
            <AnimatePresence key={`an-${index}`}>
              <motion.div
                key={`m-${index}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.4, ease: [0, 0.8, 0.4, 1] }}
              >
                <Component
                  key={`a-${index}`}
                  aria-current='page'
                  className={cn(
                    route.length > 1 ? 'text-foreground-muted' : 'text-foreground-base',
                    'hover:text-info-text'
                  )}
                  href={r.href}
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    r.onClick?.();
                  }}
                >
                  {r.locKey ? t(`breadcrumb.${r.locKey}`) : r.label}
                </Component>
              </motion.div>
            </AnimatePresence>
          )
        )}
      </nav>
    );
  }
);

Breadcrumb.displayName = 'Breadcrumb';
